import { joinClassNames } from '@vertex-protocol/web-common';
import { Divider, Icons, Input } from '@vertex-protocol/web-ui';
import { ActionSummary } from 'client/components/ActionSummary';
import { FractionAmountButtons } from 'client/components/FractionAmountButtons';
import { BridgeModeSelector } from 'client/modules/collateral/nadoDeposit/components/BridgeModeSelector';
import { NadoDepositError } from 'client/modules/collateral/nadoDeposit/components/NadoDepositError';
import { NadoDepositProgress } from 'client/modules/collateral/nadoDeposit/components/NadoDepositProgress';
import { NadoDepositSubmitButton } from 'client/modules/collateral/nadoDeposit/components/NadoDepositSubmitButton';
import { NadoFeeSummary } from 'client/modules/collateral/nadoDeposit/components/NadoFeeSummary';
import { NadoGasAlert } from 'client/modules/collateral/nadoDeposit/components/NadoGasAlert';
import { TokenIcon } from 'client/modules/collateral/nadoDeposit/components/NadoTokenSelect';
import { ChainIcon } from 'client/modules/collateral/nadoDeposit/components/NadoChainSelect';
import { DEPOSIT_CHAIN_BY_ID } from 'client/modules/collateral/nadoDeposit/config/chains';
import {
  NadoBridgeProgressData,
  NadoDepositFormActions,
  NadoDepositFormData,
} from 'client/modules/collateral/nadoDeposit/types';
import { getNadoReceiveTokenSymbol } from '../utils/getNadoReceiveTokenSymbol';

interface Props extends NadoDepositFormData, NadoDepositFormActions {
  onBack: () => void;
  /** Minimum deposit amount label (e.g. "1.00") */
  minimumDepositAmount?: string;
  /** Fraction currently selected (0.25, 0.5, 0.75, 1) or undefined */
  selectedFraction?: number;
  /** Bridge progress data, set once the OFT send tx is confirmed */
  bridgeProgress?: NadoBridgeProgressData;
}

/**
 * Step 2 of the Nado deposit flow.
 *
 * Shows:
 *  - Amount input with token icon
 *  - Chain label
 *  - Available balance (clickable to fill 100%)
 *  - Fraction buttons
 *  - Min deposit info
 *  - Gas alert (cross-chain only, when no ETH on Ink)
 *  - Fee summary (cross-chain only)
 *  - Submit button
 *  - Bridge progress (shown after OFT send is confirmed)
 */
export function NadoDepositAmountStep({
  selectedChainId,
  selectedTokenSymbol,
  amount,
  availableBalance,
  estimatedReceiveAmount,
  lzFee,
  gasAirdropAmount,
  needsGasAirdrop,
  isCorrectChain,
  buttonState,
  bridgeProvider,
  bridgeMode,
  showBridgeModeSelector,
  acrossFee,
  acrossExpectedOutput,
  acrossFillEstimateSec,
  receivedAmount,
  isAcrossQuoteLoading,
  isAcrossRouteUnavailable,
  inputError,
  errorMessage,
  errorDetails,
  isFundingAutomationAvailable,
  isFundingAutomationActive,
  onAmountChange,
  onFractionSelected,
  onBridgeModeChange,
  onSubmit,
  onStartFundingAutomation,
  onBack,
  minimumDepositAmount,
  selectedFraction,
  bridgeProgress,
}: Props) {
  const chain = DEPOSIT_CHAIN_BY_ID[selectedChainId];
  const isCrossChain = chain ? !chain.isPrimary : false;
  const sourceTokenSymbol =
    selectedTokenSymbol === 'USDT0' && isCrossChain ? 'USDT' : selectedTokenSymbol;
  const receiveTokenSymbol = getNadoReceiveTokenSymbol(selectedTokenSymbol);

  // Show fee summary for cross-chain deposits with a quoted fee
  const showFeeSummary = isCrossChain && !!lzFee && !!estimatedReceiveAmount;

  // Show gas alert for cross-chain when user has no ETH on Ink
  const showGasAlert = isCrossChain && needsGasAirdrop;

  // Show bridge progress after the OFT send tx is submitted
  const showBridgeProgress = !!bridgeProgress;

  function handleAvailableClick() {
    onFractionSelected(1);
  }

  return (
    <div className="flex flex-col gap-y-4">
      {/* Amount input */}
      <div
        className={joinClassNames(
          'bg-surface-2 flex flex-col gap-y-3 rounded-sm p-3',
        )}
      >
        {/* Token + input row */}
        <div className="flex items-center gap-x-3">
          <div className="flex items-center gap-x-2 flex-shrink-0">
            <TokenIcon symbol={sourceTokenSymbol} />
            <span className="text-text-primary text-sm font-medium">
              {sourceTokenSymbol}
            </span>
          </div>
          <Input.TextArea
            type="number"
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
            disabled={isFundingAutomationActive}
            placeholder="0.00"
            className="text-text-primary text-right text-lg focus:outline-none min-w-0"
            min={0}
            step="any"
          />
        </div>

        {/* Chain label */}
        {chain && (
          <div className="flex items-center gap-x-1.5">
            <span className="text-text-tertiary text-xs">Pay on:</span>
            <ChainIcon chainId={chain.chainId} />
            <span className="text-text-secondary text-xs">{chain.name}</span>
          </div>
        )}

        {/* Available balance */}
        <button
          type="button"
          onClick={handleAvailableClick}
          disabled={isFundingAutomationActive}
          className={joinClassNames(
            'text-text-tertiary flex items-center gap-x-1 text-xs transition-colors self-start',
            isFundingAutomationActive
              ? 'cursor-not-allowed opacity-60'
              : 'hover:text-text-secondary',
          )}
        >
          <span>Available:</span>
          <span className="text-text-secondary font-medium">
            {availableBalance} {sourceTokenSymbol}
          </span>
        </button>

        {/* Input validation error */}
        {inputError && (
          <p className="text-negative text-xs">{inputError}</p>
        )}
      </div>

      {/* Fraction buttons */}
      <FractionAmountButtons
        selectedFraction={selectedFraction}
        disabled={isFundingAutomationActive}
        onFractionSelected={onFractionSelected}
      />

      {/* Minimum deposit info */}
      {minimumDepositAmount && (
        <MinimumDepositInfo
          amount={minimumDepositAmount}
          symbol={sourceTokenSymbol}
        />
      )}

      {/* Gas alert (cross-chain + no ETH on Ink) */}
      {showGasAlert && <NadoGasAlert gasAirdropAmount={gasAirdropAmount} />}

      {/* Bridge mode selector (USDC cross-chain only, hidden during bridge progress) */}
      {showBridgeModeSelector && !showBridgeProgress && (
        <BridgeModeSelector
          selectedMode={bridgeMode}
          onModeChange={onBridgeModeChange}
          acrossFee={acrossFee}
          isLoading={isAcrossQuoteLoading}
          disabled={
            isFundingAutomationActive ||
            ['approving', 'quoting', 'bridging', 'bridge_submitted', 'bridge_pending', 'depositing', 'success'].includes(buttonState)
          }
        />
      )}

      {isAcrossRouteUnavailable && (
        <p className="text-negative text-xs">
          Across route unavailable for this token, chain, and amount.
        </p>
      )}

      {buttonState === 'partial_arrival' && receivedAmount && (
        <p className="text-warning text-xs">
          Received {receivedAmount} {selectedTokenSymbol}. Continue only with the received amount.
        </p>
      )}

      {buttonState === 'arrival_timeout' && (
        <p className="text-text-tertiary text-xs">
          Arrival has not been detected yet. Keep the bridge link visible and retry the check.
        </p>
      )}

      {/* Fee summary + submit button */}
      <ActionSummary.Container>
        {showFeeSummary && (
          <>
            <div className="px-3 pt-3">
              <NadoFeeSummary
                lzFee={lzFee}
                gasAirdropAmount={needsGasAirdrop ? gasAirdropAmount : undefined}
                estimatedReceiveAmount={estimatedReceiveAmount}
                tokenSymbol={receiveTokenSymbol}
                bridgeProvider={bridgeProvider}
                acrossExpectedOutput={acrossExpectedOutput}
                acrossFillEstimateSec={acrossFillEstimateSec}
              />
            </div>
            <Divider />
          </>
        )}
        <div className="flex flex-col gap-y-2 p-2">
          <NadoDepositSubmitButton
            state={buttonState}
            chainName={chain?.name}
            tokenSymbol={sourceTokenSymbol}
            isCrossChain={isCrossChain}
            bridgeDelivered={bridgeProgress?.status === 'delivered'}
            bridgeProvider={bridgeProvider}
            isFundingAutomationAvailable={isFundingAutomationAvailable}
            isFundingAutomationActive={isFundingAutomationActive}
            onClick={isFundingAutomationAvailable ? onStartFundingAutomation : onSubmit}
          />
          {errorMessage && (
            <NadoDepositError
              message={errorMessage}
              details={errorDetails}
            />
          )}
        </div>
      </ActionSummary.Container>

      {/* Step progress tracker */}
      <NadoDepositProgress
        state={buttonState}
        isCrossChain={isCrossChain}
        bridgeDelivered={bridgeProgress?.status === 'delivered'}
        bridgeProvider={bridgeProvider}
        sourceExplorerUrl={chain?.blockExplorerUrl}
        bridgeTxHash={bridgeProgress?.sourceTxHash}
      />
    </div>
  );
}

function MinimumDepositInfo({
  amount,
  symbol,
}: {
  amount: string;
  symbol: string;
}) {
  return (
    <div className="flex items-center gap-x-1.5">
      <Icons.CurrencyCircleDollar className="text-text-tertiary size-3.5" />
      <span className="text-text-tertiary text-xs">
        Minimum deposit:{' '}
        <span className="text-text-secondary">
          {amount} {symbol}
        </span>
      </span>
    </div>
  );
}
